import "./sentry.widget"; // ← must come first so XHR/Fetch are instrumented
import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import { configureApi } from "../../../src/lib/apiClientRuntime";
import { Widget } from "./Widget";
import { WidgetProviders } from "./providers";
import { PortalContainerProvider } from "./portal/PortalContainer";
import widgetCssUrl from "./styles/widget.css?url";


type MountOptions = {
  apiBase?: string;
  widgetHost?: string;
  theme?: "light" | "dark";
};

function mount(selectorOrEl: string | Element, opts: MountOptions = {}) {
  const el =
    typeof selectorOrEl === "string"
      ? document.querySelector(selectorOrEl)!
      : selectorOrEl;

  if (!el) {
    throw new Error("Mount target not found");
  }

  const apiBase = opts.apiBase ?? import.meta.env.VITE_API_URL ?? "https://boona.app";
  configureApi(apiBase);

  // Tag widget-specific context in Sentry (helps searching by tenant/env)
  try {
    if (opts.widgetHost) Sentry.setTag("widget.widgetHost", opts.widgetHost);
    if (apiBase) Sentry.setTag("widget.apiBase", String(apiBase));
  } catch {}

  // Create a shadow host to isolate widget styles from the host page.
  const shadowHost = document.createElement("div");
  shadowHost.className = "salon-widget-host";
  // ensure the host takes full width so the inner widget can be centered with margin:auto
  shadowHost.style.cssText = "display:block;width:100%;";
  el.appendChild(shadowHost);

  // Attach an open shadow root
  const shadowRoot = shadowHost.attachShadow({ mode: "open" });

  // Attach widget stylesheet into the shadow root so Tailwind styles are scoped to the widget.
  // The import above resolves to the emitted CSS URL during the Vite build.
  const linkEl = document.createElement("link");
  linkEl.setAttribute("rel", "stylesheet");
  linkEl.setAttribute("href", widgetCssUrl);
  shadowRoot.appendChild(linkEl);

  // Inject Inter font into the shadow root so font-sans can use it (scoped to widget).
  const fontLinkEl = document.createElement("link");
  fontLinkEl.setAttribute("rel", "stylesheet");
  fontLinkEl.setAttribute("href", "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");
  shadowRoot.appendChild(fontLinkEl);

  console.log('[embed.tsx] Creating widget with shadowRoot:', shadowRoot);

  // Set global flag to indicate widget context and provide access to portal container
  const PortalContainerWrapper = () => (
    <PortalContainerProvider container={shadowRoot as unknown as Element}>
      <Widget apiBase={opts.apiBase} widgetHost={opts.widgetHost} />
    </PortalContainerProvider>
  );

  // Make portal container available globally for UI components
  (window as any).__SALON_WIDGET_PORTAL_CONTAINER__ = {
    usePortalContainer: () => {
      // This will be called from within React context, so we need to get the current container
      // For now, return the shadowRoot directly since it's stable
      return shadowRoot as unknown as Element;
    }
  };

  const root = createRoot(shadowRoot);
  root.render(
    <WidgetProviders>
      <PortalContainerWrapper />
    </WidgetProviders>
  );

  return { unmount: () => {
    root.unmount();
    // cleanup shadow host
    if (shadowHost.parentNode) shadowHost.parentNode.removeChild(shadowHost);
  } };
}

// expose globally for <script> consumers
// @ts-ignore
window.SalonBookingWidget = { mount };
